import { createContext, useContext, useState, useCallback } from 'react';
import { useElevenLabsChat } from '../hooks/useElevenLabsChat';
import { showToast } from '../../../components/CustomToast';

const VoiceAgentContext = createContext(null);

export const VoiceAgentProvider = ({ children }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  // Handle connection loss - go to step 3 (form) without toast
  const handleConnectionLoss = useCallback(() => {
    setCurrentStep(3);
  }, []);

  // Get all ElevenLabs state from hook
  const {
    conversationId, isConnected, isConnecting, connectionError, micMuted, setMicMuted,
    conversation, isSpeaking, isListening, debouncedIsSpeaking, isReadyForAnimation,
    handleButtonClick, handleToggleMute, handleOpenChat, handleDisconnect, handleCaptureKrishnaAiData,
    userDetails, isFinalizing, interactionMode, setInteractionMode,
    chatHistory, allowTyping, clearChatHistory,pushUserMessage,waitingForAgent
  } = useElevenLabsChat(handleConnectionLoss);

  // Step navigation with animation
  const goToStep = useCallback((nextStep) => {
    if (nextStep === currentStep) return;
    setIsAnimating(true);
    setTimeout(() => {
      requestAnimationFrame(() => {
        setIsAnimating(false);
      });
      setCurrentStep(nextStep);
    }, 700);
  }, [currentStep]);

  // Initiate conversation (step 0 → 1 → connect)
  const initiateConv = useCallback((skipIntro = false) => {
    if (skipIntro === true) {
      setCurrentStep(1);
    } else {
      goToStep(1);
    }
    setInteractionMode("voice");
    setTimeout(() => {
      handleOpenChat?.();
    }, 2000);
  }, [goToStep, handleOpenChat]);

  // Handle question selection - just send the message without changing steps
  const handleSelectQuestion = useCallback((question) => {
    pushUserMessage(question);
    conversation?.sendUserMessage?.(question);
  }, [conversation]);

  // End call and move to form (disconnect automatically navigates to step 3)
  const endCallAndShowForm = useCallback(() => {
    handleDisconnect?.();
  }, [handleDisconnect]);


  const value = {
    // Step management
    currentStep, setCurrentStep, isAnimating, goToStep,pushUserMessage,

    // ElevenLabs state
    conversationId, isConnected, isConnecting, connectionError, micMuted, setMicMuted, conversation,
    isSpeaking, isListening, debouncedIsSpeaking, isReadyForAnimation, handleButtonClick, handleToggleMute, handleOpenChat,
    handleDisconnect, handleCaptureKrishnaAiData, userDetails, isFinalizing, interactionMode, setInteractionMode,waitingForAgent,

    // Chat history
    chatHistory, allowTyping, clearChatHistory,

    // Combined actions
    initiateConv, handleSelectQuestion, endCallAndShowForm,
  };

  return (
    <VoiceAgentContext.Provider value={value}>
      {children}
    </VoiceAgentContext.Provider>
  );
};

export const useVoiceAgent = () => {
  const context = useContext(VoiceAgentContext);
  if (!context) {
    throw new Error('useVoiceAgent must be used within VoiceAgentProvider');
  }
  return context;
};
